import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { NodeAccountState, ManuallyRandomizePasswordOrSshKeyParam } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';

export const actions: ActionTree<NodeAccountState, RootState> = {
    /**
     * ノードグループとアカウント検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} node_group_id ノードグループID
     * @param {any} node_id ノードID
     * @param {any} user_id 作業者ID
     * @param {any} application_form_template_id 申請書テンプレートID
     * @param {any} word 検索キーワード
     * @param {any} business_id 業務ID
     * @param {any} ignore_account_ids 検索から除外したいアカウントID
     * @param {any} sort ソート条件
     * @param {any} duplicate_application	同一操作期間申請済みノードアカウント除外情報
     * @returns {Promise<void>}
     */
    async index(
        { dispatch, commit },
        {
            pageNumber,
            perPage,
            node_group_id,
            node_id,
            user_id,
            application_form_template_id,
            word,
            business_id,
            ignore_account_ids,
            sort,
            duplicate_application,
        } = {}
    ) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            node_group_id,
            node_id,
            word,
            business_id,
            user_id,
            application_form_template_id,
            ignore_os_account_ids: ignore_account_ids.os,
            ignore_node_domain_account_assignment_ids: ignore_account_ids.domain,
            ignore_other_account_ids: ignore_account_ids.other,
            ignore_node_idp_account_assignment_ids: ignore_account_ids.idp,
            sort: { column: sort.column, direction: sort.direction },
            duplicate_application: duplicate_application ? duplicate_application : undefined,
        };
        try {
            commit(types.INDEX_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.NodeAccount.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.INDEX_SUCCESS, Object.freeze(resData));
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.INDEX_FAILURE, params);
        }
    },

    /**
     * パスワードランダム化対象のノード管理者アカウントと作業者アカウントの取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id ノードID
     * @param {any} word 検索キーワード
     * @param {any} account_type_display アカウント種別
     * @param {any} randomize_type ランダマイズ方式
     * @param {any} connection_type 認証方式
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async randomizableAccounts(
        { dispatch, commit },
        {
            id,
            word = '',
            account_type_display = '',
            randomize_type = undefined,
            connection_type = undefined,
            sort,
            pageNumber,
            perPage,
        } = {}
    ) {
        const payload = {
            word: word,
            account_type_display: account_type_display,
            randomize_type: randomize_type,
            connection_type: connection_type,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
            page_number: pageNumber,
            per_page: perPage,
        };
        try {
            commit(types.NODE_RANDOMIZABLE_ACCOUNT_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.Node.RANDOMIZABLE_ACCOUNTS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.NODE_RANDOMIZABLE_ACCOUNT_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.NODE_RANDOMIZABLE_ACCOUNT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードアカウントのパスワード・SSH鍵の手動ランダム化
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} account_type アカウント種別
     * @param {any} account_id アカウントID
     * @returns {Promise<void>}
     */
    async manuallyRandomizePasswordOrSshKey(
        { dispatch, commit, rootState },
        param: ManuallyRandomizePasswordOrSshKeyParam
    ) {
        const payload = param;
        try {
            commit(types.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.NodeAccount.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY,
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.NodeAccount.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードアカウント除外リストの取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} worker_id 作業者ID
     * @param {any} application_form_template_id 申請書テンプレートID
     * @param {any} added_account_ids 追加済みアカウントID
     * @param {any} emergency 緊急申請
     * @param {any} work_start_at 作業開始日時
     * @param {any} work_end_at 作業終了日時
     * @returns {Promise<void>}
     */
    async reservableForWork(
        { dispatch, commit },
        { worker_id, application_form_template_id, added_account_ids, emergency, work_start_at, work_end_at } = {}
    ) {
        const payload = {
            user_id: worker_id,
            application_form_template_id: application_form_template_id,
            added_os_account_ids: added_account_ids.os,
            added_node_domain_account_assignment_ids: added_account_ids.domain,
            added_other_account_ids: added_account_ids.other,
            added_node_idp_account_assignment_ids: added_account_ids.idp,
            emergency: emergency,
            work_start_at: work_start_at,
            work_end_at: work_end_at,
        };
        try {
            commit(types.RESERVABLE_FOR_WORK_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.NodeAccount.RESERVABLE_FOR_WORK,
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.RESERVABLE_FOR_WORK_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.RESERVABLE_FOR_WORK_FAILURE, resData.errors[0]);
        }
    },

    /**
     * パスワードランダム化に関するエラーのみリセットする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async resetRandomizePasswordError({ commit }) {
        commit(types.RESET_RANDOMIZE_PASSWORD_ERROR);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
